import { Button, notification } from 'antd';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CheckCircleOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import './notifier-item.scss';

import { LOCAL_STORAGE_KEY } from '~/core/constants';
import { pullLocalData } from '~/core/helpers/storageHelper';
import { GetEstimateDto } from '~/core/models/save-estimate-dto';

export enum NotifierType {
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
}

export interface SavedNotification {
  estimateNumber: string;
  notifierType: NotifierType;
  title: string;
  description: string;
  message?: string;
  duration?: number;
}

/**
 * Get and show any saved notifications for the given estimate number
 * These MUST be in localStorage, not sessionStorage!
 * @param estimateNumber the estimate number to get notifications for
 */
export const getNotificationsFor = (
  estimateNumber: string
): SavedNotification[] => {
  // remove any of my messages from the saved list
  const messages = pullLocalData<SavedNotification>(
    LOCAL_STORAGE_KEY.NOTIFICATIONS,
    (item) => item.estimateNumber === estimateNumber
  );

  messages
    .filter((item) => item.notifierType === NotifierType.SUCCESS)
    .forEach((item) => {
      showNotification(
        item.notifierType,
        item.title,
        item.description,
        item.message,
        item.duration
      );
    });

  return messages;
};

interface SimpleMessageProps {
  description: string;
  message?: string | null;
}

export const SimpleMessage = ({
  description,
  message,
}: SimpleMessageProps): JSX.Element => (
  <div className="description-container">
    <div style={{ marginBottom: '3px' }}>{description}</div>
    {message && <div>{message}</div>}
  </div>
);

interface StatusErrorProps {
  description?: string | null;
  message: string | null;
}

export const StatusError = ({
  description,
  message,
}: StatusErrorProps): JSX.Element => (
  <div className="description-container">
    {description && <div style={{ marginBottom: '10px' }}>{description}</div>}
    {message && <div>{message}</div>}
  </div>
);

interface PaymentAvailableProps {
  paymentUrl: string;
  estimate: GetEstimateDto;
  children?: ReactNode;
}

export const PaymentAvailable = ({
  paymentUrl,
  estimate,
  children,
}: PaymentAvailableProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className="description-container">
      <div style={{ marginBottom: '16rem' }}>
        {t('NOTIFICATIONS.FETCH_ESTIMATE.READY_TO_PAY.DESC', {
          estimateNumber: estimate.estimateNumber,
        })}
      </div>
      <Button
        className="payment-button"
        onClick={() => {
          window.open(paymentUrl);
        }}
      >
        {t('ORDER_SUMMARY.ESTIMATE_SECTION.PAYMENT_BUTTON')}
      </Button>
      {children ? <div style={{ margin: '16rem 0' }}>{children}</div> : null}
    </div>
  );
};

export const showNotification = (
  notifierType: NotifierType,
  title: string,
  description: string | JSX.Element,
  message = '',
  duration = 5
): void => {
  const icons = {
    success: <CheckCircleOutlined />,
    warning: <WarningOutlined />,
    error: <StopOutlined />,
  };

  const classes = {
    success: 'success-container',
    warning: 'warning-container',
    error: 'error-container',
  };

  if (typeof description === 'string') {
    notification.open({
      message: title,
      description: (
        <SimpleMessage description={description} message={message} />
      ),
      icon: icons[notifierType],
      className: classes[notifierType],
      duration: duration,
    });
  } else {
    notification.open({
      message: title,
      description: description,
      icon: icons[notifierType],
      className: classes[notifierType],
      duration: duration,
    });
  }
};
